<template>
  <div class="pm">
    <!-- ── Summary / tools ─────────────────────────────── -->
    <header class="pm-head" :class="{ 'is-open': open, 'is-static': !collapsible }">
      <component
        :is="collapsible ? 'button' : 'div'"
        :type="collapsible ? 'button' : undefined"
        class="pm-toggle"
        :aria-expanded="collapsible ? open : undefined"
        @click="collapsible && (open = !open)"
      >
        <div class="pm-head-text">
          <span class="pm-eyebrow">Sécurité</span>
          <h5 class="pm-title">Permissions d'utilisateur</h5>
          <p class="pm-sub">
            <b>{{ permStats.granted }}</b> / {{ permStats.total }} permissions accordées
            · {{ permStats.modules }} module(s) accessibles
          </p>
          <div class="pm-meter" :title="`${permStats.percent}% des permissions accordées`">
            <span :style="{ width: permStats.percent + '%' }"></span>
          </div>
        </div>
        <i v-if="collapsible" class="ph ph-caret-down pm-caret" aria-hidden="true"></i>
      </component>
      <div v-if="open" class="pm-tools">
        <div class="pm-search">
          <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
          <input
            v-model="permSearch"
            type="search"
            placeholder="Rechercher une permission…"
            aria-label="Rechercher une permission"
          />
        </div>
        <div class="pm-seg" role="group" aria-label="Afficher">
          <button
            v-for="opt in permViewOptions"
            :key="opt.value"
            type="button"
            :class="{ active: permView === opt.value }"
            :aria-pressed="permView === opt.value"
            @click="permView = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
        <div class="pm-bulk">
          <button type="button" class="btn btn-sm btn-outline-success" @click="setAllPermissions(true)">
            <i class="ph ph-check-square" aria-hidden="true"></i> Tout accorder
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="setAllPermissions(false)">
            <i class="ph ph-square" aria-hidden="true"></i> Tout retirer
          </button>
        </div>
      </div>
    </header>

    <!-- ── Module cards (accordion body, closed by default) ── -->
    <template v-if="open">
      <div v-if="!visibleGroups.length" class="pm-empty">
        <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
        <p>Aucune permission ne correspond à votre recherche.</p>
      </div>
      <div v-else class="pm-grid">
        <article
          v-for="group in visibleGroups"
          :key="group.key"
          class="pm-card"
          :class="{ 'is-on': group.check }"
        >
          <header class="pm-card-head">
            <span class="pm-avatar" aria-hidden="true">
              <i :class="group.check ? 'ph ph-lock-key-open' : 'ph ph-lock-key'"></i>
            </span>
            <div class="pm-card-title">
              <strong>{{ group.name }}</strong>
              <small>{{ group.check ? "Accès au module accordé" : "Aucun accès au module" }}</small>
            </div>
            <label class="pm-switch" :title="group.check ? 'Retirer l\'accès' : 'Accorder l\'accès'">
              <input
                type="checkbox"
                :checked="group.check"
                :aria-label="`Accès au module ${group.name}`"
                @change="setParent(group, $event.target.checked)"
              />
              <span class="pm-switch-track"><span class="pm-switch-thumb"></span></span>
            </label>
          </header>

          <div v-if="group.permissions && group.permissions.length" class="pm-card-body">
            <div class="pm-actions-head">
              <span>Actions <b>{{ group.enabled.length }}/{{ group.permissions.length }}</b></span>
              <span class="pm-links">
                <button type="button" @click="setAllInGroup(group, true)">Toutes</button>
                <button type="button" @click="setAllInGroup(group, false)">Aucune</button>
              </span>
            </div>
            <div class="pm-chips">
              <label
                v-for="perm in filteredChildren(group)"
                :key="perm.key"
                class="pm-chip"
                :class="{ 'is-on': perm.check, 'is-changed': changed(perm.key, perm.check) }"
              >
                <input
                  type="checkbox"
                  :checked="perm.check"
                  @change="setChild(group, perm, $event.target.checked)"
                />
                <i :class="perm.check ? 'ph ph-check-circle' : 'ph ph-circle'" aria-hidden="true"></i>
                <span>{{ perm.name }}</span>
              </label>
            </div>
            <p v-if="!group.check" class="pm-hint">
              <i class="ph ph-info" aria-hidden="true"></i>
              Cocher une action accorde aussi l'accès au module.
            </p>
          </div>
        </article>
      </div>
    </template>
    <p v-if="showPending && changes > 0" class="pm-pending" role="status">
      <i class="ph ph-warning" aria-hidden="true"></i>
      <b>{{ changes }}</b> modification{{ changes > 1 ? "s" : "" }} de permissions non enregistrée{{ changes > 1 ? "s" : "" }}
    </p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";
import { countChanges, isChanged, syncEnabled } from "./permissionsTree";

// Permissions picker used inside the user create / edit forms. It edits the
// `groups` tree in place; the parent form sends it on submit.
export default defineComponent({
  name: "UserPermissions",
  props: {
    groups: {
      type: Array as PropType<any[]>,
      required: true,
    },
    // key → check map of the saved state (edit form), for the change markers
    saved: {
      type: Object as PropType<Record<string, boolean>>,
      default: () => ({}),
    },
    // true = accordion closed by default (forms); false = always open (own tab)
    collapsible: {
      type: Boolean,
      default: true,
    },
    // inline "N unsaved changes" line (off when the parent shows its own save bar)
    showPending: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    // Rules:
    //  - a child can be unchecked without touching the parent;
    //  - checking a child grants the parent (an action needs module access);
    //  - removing the parent removes its children.
    function setParent(group: any, value: boolean) {
      group.check = value;
      if (!value) (group.permissions || []).forEach((p: any) => (p.check = false));
      syncEnabled(group);
    }

    function setChild(group: any, perm: any, value: boolean) {
      perm.check = value;
      if (value) group.check = true;
      syncEnabled(group);
    }

    function setAllInGroup(group: any, value: boolean) {
      if (value) group.check = true;
      (group.permissions || []).forEach((p: any) => (p.check = value));
      syncEnabled(group);
    }

    function setAllPermissions(value: boolean) {
      props.groups.forEach((group: any) => {
        group.check = value;
        (group.permissions || []).forEach((p: any) => (p.check = value));
        syncEnabled(group);
      });
    }

    const changed = (key: string, check: boolean) => isChanged(props.saved, key, check);
    const changes = computed(() => countChanges(props.groups, props.saved));

    // Summary figures
    const permStats = computed(() => {
      let total = 0;
      let granted = 0;
      let modules = 0;
      props.groups.forEach((group: any) => {
        total += 1 + (group.permissions?.length || 0);
        if (group.check) {
          granted++;
          modules++;
        }
        (group.permissions || []).forEach((p: any) => p.check && granted++);
      });
      return { total, granted, modules, percent: total ? Math.round((granted / total) * 100) : 0 };
    });

    // Accordion: starts collapsed unless used as a plain section
    const open = ref(!props.collapsible);

    // Search + view filter
    const permSearch = ref("");
    const permView = ref("all");
    const permViewOptions = [
      { value: "all", label: "Toutes" },
      { value: "on", label: "Accordées" },
      { value: "off", label: "Non accordées" },
    ];
    const norm = (s: any) =>
      String(s || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase();
    const viewOk = (item: any) =>
      permView.value === "all" || (permView.value === "on" ? !!item.check : !item.check);
    function matchPerm(item: any) {
      const q = norm(permSearch.value.trim());
      const textOk = !q || norm(item.name).includes(q) || norm(item.key).includes(q);
      return textOk && viewOk(item);
    }
    function filteredChildren(group: any) {
      const q = permSearch.value.trim();
      // module name matches the search → show all its actions (view filter still applies)
      if (q && norm(group.name).includes(norm(q))) {
        return (group.permissions || []).filter(viewOk);
      }
      return (group.permissions || []).filter(matchPerm);
    }
    const visibleGroups = computed(() =>
      props.groups.filter((group: any) => matchPerm(group) || filteredChildren(group).length > 0)
    );

    return {
      open,
      setParent,
      setChild,
      setAllInGroup,
      setAllPermissions,
      changed,
      changes,
      permStats,
      permSearch,
      permView,
      permViewOptions,
      filteredChildren,
      visibleGroups,
    };
  },
});
</script>

<style scoped>
/* ── Permissions section (tokens from assets/css/theme/_tokens.scss) ── */
.pm {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--ui-space-5);
  padding-bottom: var(--ui-space-2);
}
.pm-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--ui-space-4);
  padding: var(--ui-space-5) var(--ui-space-6);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.pm-toggle {
  display: flex;
  align-items: center;
  gap: var(--ui-space-4);
  flex: 1 1 100%;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.pm-head.is-static .pm-toggle {
  cursor: default;
}
.pm-toggle:focus-visible {
  outline: none;
  box-shadow: var(--ui-focus-ring);
  border-radius: var(--ui-radius-sm);
}
.pm-caret {
  flex: 0 0 auto;
  color: var(--ui-text-muted);
  font-size: 20px;
  transition: transform var(--ui-dur) var(--ui-ease);
}
.pm-head.is-open .pm-caret {
  transform: rotate(180deg);
}
.pm-head-text {
  flex: 1 1 260px;
  min-width: 0;
}
.pm-eyebrow {
  display: block;
  margin-bottom: 2px;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-black);
  letter-spacing: var(--ui-tracking-eyebrow);
  text-transform: uppercase;
}
.pm-title {
  margin: 0;
  font-size: var(--ui-fs-lg);
  font-weight: var(--ui-fw-black);
}
.pm-sub {
  margin: 4px 0 10px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.pm-sub b {
  color: var(--ui-text);
}
.pm-meter {
  max-width: 360px;
  height: 6px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  overflow: hidden;
}
.pm-meter span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--ui-accent);
  transition: width 500ms var(--ui-ease-spring);
}
.pm-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.pm-search {
  position: relative;
  width: 240px;
}
.pm-search i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ui-text-faint);
  pointer-events: none;
}
.pm-search input {
  width: 100%;
  height: 40px;
  padding: 0 14px 0 38px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  outline: none;
  transition: border-color var(--ui-dur) var(--ui-ease), box-shadow var(--ui-dur) var(--ui-ease),
    background-color var(--ui-dur) var(--ui-ease);
}
.pm-search input:focus {
  border-color: var(--ui-accent);
  background: var(--ui-surface);
  box-shadow: var(--ui-focus-ring);
}
.pm-seg {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
}
.pm-seg button {
  padding: 6px 12px;
  border: 0;
  border-radius: var(--ui-radius-pill);
  background: transparent;
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  white-space: nowrap;
}
.pm-seg button.active {
  background: var(--ui-surface);
  color: var(--ui-text);
  box-shadow: 0 2px 8px rgba(28, 25, 23, 0.1);
}
.pm-bulk {
  display: flex;
  gap: 8px;
}

/* Module cards */
.pm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: var(--ui-space-4);
  align-items: start;
}
.pm-card {
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
  overflow: hidden;
  transition: border-color var(--ui-dur) var(--ui-ease), box-shadow var(--ui-dur) var(--ui-ease);
}
.pm-card.is-on {
  border-color: rgba(var(--ui-accent-rgb), 0.45);
}
.pm-card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--ui-surface-sunken);
  transition: background-color var(--ui-dur) var(--ui-ease);
}
.pm-card.is-on .pm-card-head {
  background: var(--ui-accent-soft);
}
.pm-avatar {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: var(--ui-radius-sm);
  background: var(--ui-surface);
  color: var(--ui-text-muted);
  font-size: 18px;
  transition: background-color var(--ui-dur) var(--ui-ease), color var(--ui-dur) var(--ui-ease);
}
.pm-card.is-on .pm-avatar {
  background: var(--ui-accent);
  color: #FFFFFF;
}
.pm-card-title {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.pm-card-title strong {
  overflow: hidden;
  color: var(--ui-text);
  font-size: var(--ui-fs-base);
  font-weight: var(--ui-fw-black);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pm-card-title small {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
}
.pm-card.is-on .pm-card-title small {
  color: var(--ui-accent-text);
}

/* Switch */
.pm-switch {
  position: relative;
  flex: 0 0 auto;
  cursor: pointer;
}
.pm-switch input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}
.pm-switch-track {
  display: block;
  width: 46px;
  height: 26px;
  padding: 3px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-border-strong);
  transition: background-color var(--ui-dur) var(--ui-ease);
}
.pm-switch-thumb {
  display: block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #FFFFFF;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: transform var(--ui-dur) var(--ui-ease-spring);
}
.pm-switch input:checked + .pm-switch-track {
  background: var(--ui-accent);
}
.pm-switch input:checked + .pm-switch-track .pm-switch-thumb {
  transform: translateX(20px);
}
.pm-switch input:focus-visible + .pm-switch-track {
  box-shadow: var(--ui-focus-ring);
}

.pm-card-body {
  padding: 12px 16px 16px;
}
.pm-actions-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  text-transform: uppercase;
  letter-spacing: var(--ui-tracking-label);
}
.pm-actions-head b {
  color: var(--ui-text);
}
.pm-links {
  display: flex;
  gap: 4px;
}
.pm-links button {
  padding: 3px 8px;
  border: 0;
  border-radius: var(--ui-radius-xs);
  background: transparent;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  text-transform: none;
  letter-spacing: 0;
}
.pm-links button:hover {
  background: var(--ui-accent-soft);
}
.pm-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pm-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px 6px 9px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
  cursor: pointer;
  user-select: none;
  transition: all var(--ui-dur-fast) var(--ui-ease);
}
.pm-chip input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}
.pm-chip i {
  color: var(--ui-text-faint);
  font-size: 16px;
}
.pm-chip:hover {
  border-color: var(--ui-accent);
  color: var(--ui-text);
}
.pm-chip.is-on {
  border-color: var(--ui-success);
  background: var(--ui-success-soft);
  color: var(--ui-text);
}
.pm-chip.is-on i {
  color: var(--ui-success);
}
.pm-chip.is-changed::after {
  content: "";
  position: absolute;
  top: -3px;
  right: -3px;
  width: 9px;
  height: 9px;
  border: 2px solid var(--ui-surface);
  border-radius: 50%;
  background: var(--ui-accent);
}
.pm-chip:has(input:focus-visible) {
  box-shadow: var(--ui-focus-ring);
}
.pm-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.pm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 16px;
  border: 1px dashed var(--ui-border-strong);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface-sunken);
  color: var(--ui-text-muted);
}
.pm-empty i {
  font-size: 28px;
  color: var(--ui-accent-text);
}
.pm-empty p {
  margin: 0;
}

@media (max-width: 767.98px) {
  .pm-head {
    padding: var(--ui-space-4);
  }
  .pm-search,
  .pm-tools {
    width: 100%;
  }
  .pm-bulk {
    width: 100%;
  }
  .pm-bulk .btn {
    flex: 1 1 0;
  }
  .pm-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
.pm-pending {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-semibold);
}
.pm-pending i {
  color: var(--ui-warning-strong);
  font-size: 18px;
}
</style>
